/**
 * menuBar.js — Menu bar initialization (File, Edit, Arrange).
 *
 * Sets up File, Edit, and Arrange dropdowns.
 * Follows the same dropdown pattern as the color picker:
 *   - CSS .open class toggled by JS
 *   - document-level outside-click closes all dropdowns (single listener)
 */

/**
 * Initialize the menu bar with callbacks for each menu item.
 *
 * @param {{
 *   onNew: function,
 *   onOpen: function,
 *   onSaveAs: function,
 *   onOpenUnderlay: function,
 *   onRemoveUnderlay: function,
 *   onUndo: function,
 *   onRedo: function,
 *   onDelete: function,
 *   onBringToFront: function,
 *   onBringForward: function,
 *   onSendBackward: function,
 *   onSendToBack: function
 * }} callbacks
 */
export function initMenuBar({
  onNew,
  onOpen,
  onSaveAs,
  onOpenUnderlay,
  onRemoveUnderlay,
  onUndo,
  onRedo,
  onDelete,
  onBringToFront,
  onBringForward,
  onSendBackward,
  onSendToBack,
}) {
  // ── File menu ──
  const menuFileBtn       = document.getElementById("menu-file");
  const fileDropdown      = document.getElementById("menu-file-dropdown");
  const itemNew           = document.getElementById("menu-new");
  const itemOpen          = document.getElementById("menu-open");
  const itemSaveAs        = document.getElementById("menu-save-as");
  const itemOpenUnderlay  = document.getElementById("menu-open-underlay");
  const itemRemoveUnderlay = document.getElementById("menu-remove-underlay");

  // ── Edit menu ──
  const menuEditBtn       = document.getElementById("menu-edit");
  const editDropdown      = document.getElementById("menu-edit-dropdown");
  const itemUndo          = document.getElementById("menu-undo");
  const itemRedo          = document.getElementById("menu-redo");
  const itemDelete        = document.getElementById("menu-delete");

  // ── Arrange menu ──
  const menuArrangeBtn    = document.getElementById("menu-arrange");
  const arrangeDropdown   = document.getElementById("menu-arrange-dropdown");
  const itemBringToFront  = document.getElementById("menu-bring-to-front");
  const itemBringForward  = document.getElementById("menu-bring-forward");
  const itemSendBackward  = document.getElementById("menu-send-backward");
  const itemSendToBack    = document.getElementById("menu-send-to-back");

  const allDropdowns = [fileDropdown, editDropdown, arrangeDropdown].filter(Boolean);

  function closeAll() {
    allDropdowns.forEach(d => d.classList.remove("open"));
  }

  function toggleDropdown(dropdown, e) {
    e.stopPropagation();
    const isOpen = dropdown.classList.contains("open");
    closeAll();
    if (!isOpen) dropdown.classList.add("open");
  }

  // Single document listener closes all dropdowns on outside click
  document.addEventListener("click", closeAll);

  // ── File menu wiring ──
  if (menuFileBtn && fileDropdown) {
    menuFileBtn.addEventListener("click", (e) => toggleDropdown(fileDropdown, e));
  }
  if (itemNew)    itemNew.addEventListener("click",    () => { closeAll(); onNew && onNew(); });
  if (itemOpen)   itemOpen.addEventListener("click",   () => { closeAll(); onOpen && onOpen(); });
  if (itemSaveAs) itemSaveAs.addEventListener("click", () => { closeAll(); onSaveAs && onSaveAs(); });
  if (itemOpenUnderlay && onOpenUnderlay) {
    itemOpenUnderlay.addEventListener("click", () => { closeAll(); onOpenUnderlay(); });
  }
  if (itemRemoveUnderlay && onRemoveUnderlay) {
    itemRemoveUnderlay.addEventListener("click", () => { closeAll(); onRemoveUnderlay(); });
  }

  // ── Edit menu wiring ──
  if (menuEditBtn && editDropdown) {
    menuEditBtn.addEventListener("click", (e) => toggleDropdown(editDropdown, e));
  }
  if (itemUndo) itemUndo.addEventListener("click", () => { closeAll(); onUndo && onUndo(); });
  if (itemRedo) itemRedo.addEventListener("click", () => { closeAll(); onRedo && onRedo(); });
  if (itemDelete) itemDelete.addEventListener("click", () => { closeAll(); onDelete && onDelete(); });

  // ── Arrange menu wiring ──
  if (menuArrangeBtn && arrangeDropdown) {
    menuArrangeBtn.addEventListener("click", (e) => toggleDropdown(arrangeDropdown, e));
  }
  if (itemBringToFront) itemBringToFront.addEventListener("click", () => { closeAll(); onBringToFront && onBringToFront(); });
  if (itemBringForward) itemBringForward.addEventListener("click", () => { closeAll(); onBringForward && onBringForward(); });
  if (itemSendBackward) itemSendBackward.addEventListener("click", () => { closeAll(); onSendBackward && onSendBackward(); });
  if (itemSendToBack)   itemSendToBack.addEventListener("click",   () => { closeAll(); onSendToBack && onSendToBack(); });
}

/**
 * Sync enabled/disabled state of Edit and Arrange menu items.
 *
 * @param {{ canUndo: boolean, canRedo: boolean, hasSelection: boolean }} state
 */
export function syncEditArrangeMenus({ canUndo, canRedo, hasSelection }) {
  const itemUndo         = document.getElementById("menu-undo");
  const itemRedo         = document.getElementById("menu-redo");
  const itemDelete       = document.getElementById("menu-delete");
  const itemBringToFront = document.getElementById("menu-bring-to-front");
  const itemBringForward = document.getElementById("menu-bring-forward");
  const itemSendBackward = document.getElementById("menu-send-backward");
  const itemSendToBack   = document.getElementById("menu-send-to-back");

  if (itemUndo)         itemUndo.disabled         = !canUndo;
  if (itemRedo)         itemRedo.disabled         = !canRedo;
  if (itemDelete)       itemDelete.disabled       = !hasSelection;
  if (itemBringToFront) itemBringToFront.disabled = !hasSelection;
  if (itemBringForward) itemBringForward.disabled = !hasSelection;
  if (itemSendBackward) itemSendBackward.disabled = !hasSelection;
  if (itemSendToBack)   itemSendToBack.disabled   = !hasSelection;
}

/**
 * Sync enabled/disabled state of File menu items.
 *
 * @param {{ hasUnderlay: boolean }} state
 */
export function syncFileMenu({ hasUnderlay }) {
  const btn = document.getElementById("menu-remove-underlay");
  if (btn) btn.disabled = !hasUnderlay;
}
